/**
 * useEInvoice — observes the local e-invoice record for an invoice and
 * exposes a `generate()` action that POSTs to the backend then polls
 * the status endpoint until GENERATED/FAILED. Local DB rows arrive via
 * CRDT sync after the backend persists the IRN.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { EInvoice } from '@/db/models/System';
import { useObservable } from '@/hooks/useObservable';
import {
  triggerGeneration,
  getStatus,
  type EInvoiceStatus,
  type EInvoiceStatusResponse,
} from '@/services/EInvoiceService';
import { syncDatabase } from '@/sync';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 10;

export interface UseEInvoiceResult {
  /** Most-recent local e-invoice row, if any. */
  local: EInvoice | null;
  /** Latest backend status (null until first poll/fetch). */
  remote: EInvoiceStatusResponse | null;
  /** Effective status — prefers remote when present, else local. */
  status: EInvoiceStatus;
  /** True while a generation request or poll is in flight. */
  busy: boolean;
  /** Error from the last action, cleared on next call. */
  error: string | null;
  /** Trigger generation + start polling. */
  generate: () => Promise<void>;
  /** Refresh status from the backend without triggering generation. */
  refresh: () => Promise<void>;
}

export function useEInvoice(invoiceId: string | null): UseEInvoiceResult {
  const localRows = useObservable<EInvoice[]>(
    () => {
      if (!invoiceId) return null;
      return database.get<EInvoice>('e_invoices')
        .query(Q.where('invoice_id', invoiceId), Q.where('is_deleted', false))
        .observe();
    },
    [invoiceId],
    [],
  );

  const local = localRows[0] ?? null;
  const [remote, setRemote] = useState<EInvoiceStatusResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    attemptsRef.current = 0;
  }, []);

  // Cleanup on unmount or invoice change
  useEffect(() => () => stopPolling(), [stopPolling, invoiceId]);

  const refresh = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const data = await getStatus(invoiceId);
      setRemote(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch status');
    }
  }, [invoiceId]);

  const generate = useCallback(async () => {
    if (!invoiceId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await triggerGeneration(invoiceId);
      if (!res.enqueued) {
        // Already generated, or not eligible — refresh once and stop.
        await refresh();
        return;
      }
      stopPolling();
      pollRef.current = setInterval(() => {
        attemptsRef.current += 1;
        getStatus(invoiceId)
          .then((data) => {
            setRemote(data);
            if (data.status === 'GENERATED' || data.status === 'FAILED') {
              stopPolling();
              setBusy(false);
              // Pull the freshly-persisted row down via CRDT sync so the
              // observable picks it up locally.
              void syncDatabase().catch(() => undefined);
            } else if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
              stopPolling();
              setBusy(false);
            }
          })
          .catch((e) => {
            setError(e?.message || 'Polling error');
            if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
              stopPolling();
              setBusy(false);
            }
          });
      }, POLL_INTERVAL_MS);
    } catch (e: any) {
      setError(e?.message || 'Failed to trigger generation');
      setBusy(false);
    }
  }, [invoiceId, refresh, stopPolling]);

  // Effective status
  const status: EInvoiceStatus = (
    remote?.status ?? (local?.status as EInvoiceStatus | undefined) ?? 'NOT_GENERATED'
  );

  return { local, remote, status, busy, error, generate, refresh };
}
